/* Shared KOL campaign tracking. Private attributes are never read through Ajax. */
(() => {
  'use strict';
  const script = document.querySelector('script[data-aphe-kol]');
  if (!script || window.apheKolAttribution) return;
  const PREFIX = 'aphe.kol.v2.';
  const LEGACY_KEY = 'aphe.kol.v1';
  // Temporary compatibility for the already-running 2026 Alice85 campaign only.
  const LEGACY = { campaignId: 'alice85_20261027', kolCode: 'alice85',
    collectionId: '317935845445', startDate: '2026-10-27', endDate: '2026-11-01' };
  const root = script.dataset.cartRoot;
  const enteredAt = new Date().toISOString();
  const memory = new Map();
  let recordedEntry = false;
  let inFlight = null;
  let lastSignature = null;
  let replaying = false;
  const busy = new WeakSet();
  const parse = (raw) => { try { return JSON.parse(raw); } catch (_) { return null; } };
  const entry = parse(script.dataset.apheKol);
  const code = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9_]{0,63}$/.test(value);
  function day(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
    const utc = Date.parse(value + 'T00:00:00.000Z');
    return Number.isFinite(utc) && new Date(utc).toISOString().slice(0, 10) === value ? utc - 28800000 : NaN;
  }
  function valid(record, now = Date.now()) {
    if (!record || !code(record.campaignId) || !code(record.kolCode)
        || !/^\d+$/.test(record.collectionId)) return false;
    const start = day(record.startDate), end = day(record.endDate) + 86400000;
    const visit = Date.parse(record.lastVisitAt);
    return Number.isFinite(start) && Number.isFinite(end) && start < end
      && Number.isFinite(visit) && visit >= start && visit < end
      && now >= visit && now < end;
  }
  const keyFor = (record) => PREFIX + record.collectionId + '.' + record.campaignId;
  function allowed() {
    return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  }
  function save(record) {
    const key = keyFor(record);
    memory.set(key, record);
    try { localStorage.setItem(key, JSON.stringify(record)); } catch (_) { /* Same-page fallback. */ }
  }
  function remove(key) {
    memory.delete(key);
    try { localStorage.removeItem(key); } catch (_) { /* Storage unavailable. */ }
  }
  function storageKeys() {
    try { return Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter(k => k?.startsWith(PREFIX)); }
    catch (_) { return Array.from(memory.keys()); }
  }
  function clearRecords() {
    for (const key of new Set([...storageKeys(), ...memory.keys(), LEGACY_KEY])) remove(key);
  }
  function records() {
    if (!allowed()) { clearRecords(); return []; }
    // One key per activity prevents tabs visiting different campaigns overwriting each other.
    const found = new Map();
    for (const key of new Set([...storageKeys(), ...memory.keys()])) {
      let record;
      try { record = parse(localStorage.getItem(key)) || memory.get(key); } catch (_) { record = memory.get(key); }
      if (valid(record) && key === keyFor(record)) found.set(key, record);
      else remove(key);
    }
    let old;
    try { old = parse(localStorage.getItem(LEGACY_KEY)); } catch (_) { /* No legacy storage. */ }
    const migrated = { ...LEGACY, lastVisitAt: old?.lastVisitAt };
    if (old?.source === 'alice85' && valid(migrated)) {
      const key = keyFor(migrated), prior = found.get(key);
      if (!prior || Date.parse(prior.lastVisitAt) < Date.parse(migrated.lastVisitAt)) {
        save(migrated); found.set(key, migrated);
      }
    } else if (old) remove(LEGACY_KEY);
    return Array.from(found.values()).sort((a, b) => keyFor(a).localeCompare(keyFor(b)));
  }
  function attributes() {
    const active = records();
    const legacy = active.find(r => r.campaignId === LEGACY.campaignId && r.collectionId === LEGACY.collectionId);
    return {
      aphe_kol_visits__: active.length ? JSON.stringify({ version: 2, visits: active.map(({ campaignId, collectionId, lastVisitAt }) => ({ campaignId, collectionId, lastVisitAt })) }) : '',
      aphe_kol_source__: legacy ? 'alice85' : '',
      aphe_kol_last_visit_at__: legacy?.lastVisitAt || ''
    };
  }
  function recordEntry() {
    if (!entry || entry.enabled !== true || recordedEntry || !allowed()) return;
    const record = { ...entry, lastVisitAt: enteredAt };
    if (!valid(record)) return;
    const prior = records().find(r => keyFor(r) === keyFor(record));
    if (prior && Date.parse(prior.lastVisitAt) > Date.parse(record.lastVisitAt)) record.lastVisitAt = prior.lastVisitAt;
    save(record);
    if (record.campaignId === LEGACY.campaignId && record.collectionId === LEGACY.collectionId) {
      try { localStorage.setItem(LEGACY_KEY, JSON.stringify({ source: 'alice85', lastVisitAt: record.lastVisitAt })); } catch (_) { /* Same-page fallback. */ }
    }
    recordedEntry = true;
  }
  const privacyReady = new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy) { resolve(); return; }
    const timer = setTimeout(resolve, 1500);
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], () => {
        clearTimeout(timer);
        recordEntry();
        resolve();
        // Also synchronize if the API arrived after our checkout wait timed out.
        Promise.resolve().then(() => sync());
      });
    }
  });
  async function post(value) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    try {
      const result = await fetch(`${root}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: value })
      });
      if (!result.ok) throw new Error(`Cart attribute update: ${result.status}`);
    } finally { clearTimeout(timer); }
  }
  async function sync() {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      await privacyReady;
      recordEntry();
      // Refresh the snapshot on retry so another tab/consent change cannot restore old data.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const value = attributes();
        try {
          await post(value);
          lastSignature = JSON.stringify(value);
          if (lastSignature === JSON.stringify(attributes())) return true;
        } catch (_) { /* Retry once; attribution must never prevent a purchase. */ }
      }
      return false;
    })().finally(() => { inFlight = null; });
    return inFlight;
  }
  function patchForm(form) {
    // Native cart POST is an additional guarantee if Ajax fails or another cart writer races.
    for (const [key, value] of Object.entries(attributes())) {
      const name = `attributes[${key}]`;
      let input = Array.from(form.elements).find((el) => el.name === name);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden'; input.name = name; form.appendChild(input);
      }
      input.value = value;
    }
  }
  async function beforeCheckout(event, form, resume) {
    if (replaying || event.defaultPrevented) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy.has(form)) return;
    busy.add(form);
    form.setAttribute('aria-busy', 'true');
    try {
      await sync();
      if (!form.isConnected) return;
      patchForm(form);
      replaying = true;
      // Replay real controls, not form.submit(): all existing validation still runs.
      resume();
    } finally {
      replaying = false;
      busy.delete(form);
      form.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('#cartform [name="checkout"]');
    if (!button || button.disabled || event.button !== 0) return;
    void beforeCheckout(event, button.form, () => {
      const current = button.isConnected ? button : button.form?.querySelector('[name="checkout"]');
      current?.click();
    });
  }, true);
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.id !== 'cartform' || event.submitter?.name === 'update') return;
    const submitter = event.submitter || form.querySelector('[name="checkout"]');
    if (!submitter) return;
    void beforeCheckout(event, form, () => form.requestSubmit(submitter));
  }, true);
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { clearRecords(); recordedEntry = false; }
    void sync();
  });
  window.addEventListener('storage', (event) => {
    if (event.key === null) memory.clear();
    else if (event.key?.startsWith(PREFIX)) memory.delete(event.key);
    if (event.key === LEGACY_KEY || event.key?.startsWith(PREFIX) || event.key === null) void sync();
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) void sync(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && lastSignature !== JSON.stringify(attributes())) void sync();
  });
  window.apheKolAttribution = Object.freeze({ sync });
  void sync();
})();
